import { site } from "@/data/site";

export interface CustomRequest {
  interest: string;
  recipient: string;
  occasion: string;
  style: string;
  materials: string[];
  personalization: string[];
  personalizationDetail: string;
  completionDate: string;
  budget: string;
  quantity: string;
  inspirationFiles: string[];
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contactMethod: string;
  notes: string;
  reference?: string;
}

export type SubmitResult =
  | { status: "delivered" }
  | { status: "not_configured"; mailto: string; summary: string }
  | { status: "error"; message: string };

export const UPLOAD_LIMITS = {
  accept: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
  acceptLabel: "JPEG, PNG, WebP or PDF",
  maxFiles: 5,
  maxSizeMb: 10,
};

export function buildSummary(data: CustomRequest): string {
  const lines: [string, string][] = [
    ["What Paul would make", data.interest],
    ["Who it is for", data.recipient],
    ["Occasion", data.occasion],
    ["Style", data.style],
    ["Materials", data.materials.join(", ")],
    ["Personalization", [...data.personalization, data.personalizationDetail].filter(Boolean).join(" — ")],
    ["Desired completion", data.completionDate || "Flexible"],
    ["Budget", data.budget],
    ["Quantity", data.quantity],
    ["Inspiration files", data.inspirationFiles.join(", ") || "None attached"],
    ["Name", `${data.firstName} ${data.lastName}`.trim()],
    ["Email", data.email],
    ["Telephone", data.phone || "Not provided"],
    ["Preferred contact", data.contactMethod],
    ["Notes", data.notes || "None"],
  ];
  if (data.reference) lines.push(["Inspired by", data.reference]);
  return lines.map(([k, v]) => `${k}: ${v}`).join("\n");
}

/**
 * Delivery adapter.
 *
 * Requests are sent server-side via a TanStack server function that emails
 * Paul and sends the visitor a confirmation. If that call fails for any
 * reason, the visitor is handed a pre-filled email as a fallback so the
 * request is never silently lost.
 */
export async function submitCustomRequest(data: CustomRequest): Promise<SubmitResult> {
  const summary = buildSummary(data);
  const subject = `Custom request \u2014 ${data.interest} for ${data.recipient}`;
  const mailto = `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(summary)}`;

  try {
    const { sendCustomRequest } = await import("@/lib/custom-request.functions");
    const res = await sendCustomRequest({
      data: {
        name: `${data.firstName} ${data.lastName}`.trim() || "Website visitor",
        email: data.email,
        phone: data.phone ?? "",
        interest: data.interest ?? "",
        summary,
      },
    });
    if (res?.ok) return { status: "delivered" };
    return { status: "not_configured", mailto, summary };
  } catch (e) {
    console.error("custom request delivery failed", e);
    return { status: "not_configured", mailto, summary };
  }
}
